import { computeHoursFromTimeRange } from "@/lib/employeeTimes";

/** Ruhepausen zählen nur ab 15 Minuten Dauer (§ 4 ArbZG). */
export const MIN_BREAK_SEGMENT_MINUTES = 15;

/** Mindestlänge der Mitarbeiterbegründung (nach Trim). */
export const MIN_JUSTIFICATION_LENGTH = 50;

/** Ab diesem Arbeitstag greifen Begründungspflicht und Zähl-Sperre. */
export const JUSTIFICATION_EFFECTIVE_DATE = "2026-08-10";

export function justificationRegimeApplies(workDateIso: string): boolean {
  return workDateIso.slice(0, 10) >= JUSTIFICATION_EFFECTIVE_DATE;
}

export const JUSTIFICATION_EMPTY_MSG =
  "Bitte gib eine Begründung für den Arbeitszeitverstoß ein.";
export const JUSTIFICATION_TOO_SHORT_MSG =
  "Deine Begründung ist noch zu kurz. Bitte gib mindestens 50 Zeichen ein.";
export const JUSTIFICATION_HINT =
  "Bitte beschreibe konkret, warum du die Arbeitszeit- oder Pausenregel an diesem Tag nicht einhalten konntest.\n\nDeine Begründung dokumentiert nur den Grund für den Verstoß. Sie hebt den Arbeitszeitverstoß nicht auf und gilt nicht als nachträgliche Genehmigung.";

export interface TimeComplianceRow {
  rowKind: "work" | "break";
  timeFrom: string;
  timeTo: string;
}

export interface TimeComplianceViolation {
  code: string;
  title?: string;
  message: string;
  work_hours?: number;
  break_minutes?: number;
  required_break_minutes?: number;
  actual_break_minutes?: number;
}

export interface EmployeeTimeDayCompliance {
  user_login: string;
  work_date: string;
  violations: TimeComplianceViolation[];
  work_hours: number | null;
  break_minutes: number | null;
  approval_status: "pending" | "approved" | "rejected" | "justified" | null;
  approval_id: number | null;
  reviewed_by_display: string | null;
  reviewed_at: string | null;
  review_comment: string | null;
  /** Legacy: GF-Freigabe entfällt – immer false. */
  requires_approval: boolean;
  employee_justification: string | null;
  justification_created_at: string | null;
  justification_updated_at: string | null;
  justification_by_login: string | null;
  justification_by_display: string | null;
  has_justification: boolean;
  requires_justification: boolean;
  justification_locked: boolean;
  historical_gf_review: boolean;
  justification_regime_applies?: boolean;
  counts_in_totals?: boolean;
}

function rowHours(row: TimeComplianceRow): number {
  if (!row.timeFrom || !row.timeTo) return 0;
  return computeHoursFromTimeRange(row.timeFrom, row.timeTo);
}

export function sumWorkHours(rows: TimeComplianceRow[]): number {
  return rows
    .filter((r) => r.rowKind === "work" && r.timeFrom && r.timeTo)
    .reduce((sum, r) => sum + rowHours(r), 0);
}

export function sumCountedBreakMinutes(rows: TimeComplianceRow[]): number {
  return rows
    .filter((r) => r.rowKind === "break" && r.timeFrom && r.timeTo)
    .reduce((sum, r) => {
      const minutes = Math.round(rowHours(r) * 60);
      return minutes >= MIN_BREAK_SEGMENT_MINUTES ? sum + minutes : sum;
    }, 0);
}

export function formatHoursHm(hours: number): string {
  const totalMinutes = Math.max(0, Math.round(hours * 60));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

export function detectDayViolations(rows: TimeComplianceRow[]): TimeComplianceViolation[] {
  const workHours = sumWorkHours(rows);
  const breakMinutes = sumCountedBreakMinutes(rows);
  const violations: TimeComplianceViolation[] = [];

  if (workHours <= 0) return violations;

  const workLabel = formatHoursHm(workHours);

  if (workHours > 10) {
    violations.push({
      code: "exceeds_max_daily_hours",
      title: "Höchstarbeitszeit überschritten",
      message: `Du hast an diesem Tag gegen eine Arbeitszeitregel verstoßen. Deine Nettoarbeitszeit beträgt ${workLabel} Stunden und liegt über der zulässigen Höchstarbeitszeit von 10:00 Stunden (§ 3 ArbZG).`,
      work_hours: workHours,
      break_minutes: breakMinutes,
      actual_break_minutes: breakMinutes,
    });
  }

  // § 4 ArbZG: Pause ab mehr als 6 Std. (≥30 Min.) bzw. mehr als 9 Std. (≥45 Min.).
  if (workHours > 9 && breakMinutes < 45) {
    violations.push({
      code: "insufficient_break_9h",
      title: "Pause unterschritten",
      message: `Du hast an diesem Tag gegen eine Arbeitszeitregel verstoßen. Es wurden bei ${workLabel} Stunden Arbeitszeit nur ${breakMinutes} Minuten Pause erfasst. Erforderlich wären mindestens 45 Minuten (§ 4 ArbZG).`,
      required_break_minutes: 45,
      actual_break_minutes: breakMinutes,
      work_hours: workHours,
    });
  } else if (workHours > 6 && breakMinutes < 30) {
    violations.push({
      code: "insufficient_break_6h",
      title: "Pause unterschritten",
      message: `Du hast an diesem Tag gegen eine Arbeitszeitregel verstoßen. Es wurden bei ${workLabel} Stunden Arbeitszeit nur ${breakMinutes} Minuten Pause erfasst. Erforderlich wären mindestens 30 Minuten (§ 4 ArbZG).`,
      required_break_minutes: 30,
      actual_break_minutes: breakMinutes,
      work_hours: workHours,
    });
  }

  return violations;
}

export function normalizeJustification(text: string | null | undefined): string {
  return (text ?? "").trim();
}

export function validateJustificationText(text: string | null | undefined): string | null {
  const normalized = normalizeJustification(text);
  if (!normalized) return JUSTIFICATION_EMPTY_MSG;
  if (normalized.length < MIN_JUSTIFICATION_LENGTH) return JUSTIFICATION_TOO_SHORT_MSG;
  return null;
}

export function isJustificationValid(text: string | null | undefined): boolean {
  return validateJustificationText(text) === null;
}

export function formatJustificationCounter(text: string): string {
  const len = normalizeJustification(text).length;
  return `${len} / mindestens ${MIN_JUSTIFICATION_LENGTH} Zeichen`;
}

export function formatViolationStatus(compliance: EmployeeTimeDayCompliance | null): {
  label: string;
  variant: "error" | "warning" | "success";
  detail?: string;
} | null {
  if (!compliance || compliance.violations.length === 0) {
    if (compliance?.historical_gf_review) {
      return {
        label: "Historische GF-Freigabe",
        variant: "success",
        detail: compliance.reviewed_by_display
          ? `Früher freigegeben von ${compliance.reviewed_by_display}${
              compliance.reviewed_at
                ? ` am ${new Date(compliance.reviewed_at).toLocaleString("de-DE")}`
                : ""
            }.`
          : "Dieser Eintrag enthält eine historische Freigabe der Geschäftsführung.",
      };
    }
    return null;
  }

  if (compliance.requires_justification || !compliance.has_justification) {
    const legacyApproved =
      compliance.historical_gf_review && compliance.approval_status === "approved";
    if (legacyApproved) {
      return {
        label: "Arbeitszeitverstoß – historisch freigegeben",
        variant: "error",
        detail: compliance.reviewed_by_display
          ? `Historische GF-Freigabe von ${compliance.reviewed_by_display}. Der Verstoß bleibt dokumentiert.`
          : "Historische GF-Freigabe. Der Verstoß bleibt dokumentiert.",
      };
    }
    return {
      label: "Arbeitszeitverstoß",
      variant: "error",
      detail: "Deine Begründung fehlt noch.",
    };
  }

  return {
    label: "Arbeitszeitverstoß – Begründung hinterlegt",
    variant: "error",
    detail: "Du hast eine Begründung hinterlegt.",
  };
}

/** @deprecated GF-Freigabe entfällt – bitte formatViolationStatus nutzen. */
export function formatApprovalStatus(
  status: EmployeeTimeDayCompliance["approval_status"],
): string | null {
  switch (status) {
    case "pending":
      return "Begründung ausstehend";
    case "justified":
      return "Begründung hinterlegt";
    case "approved":
      return "Historische GF-Freigabe";
    case "rejected":
      return "Historisch abgelehnt";
    default:
      return null;
  }
}
